import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Flex,
    Heading,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    Spinner,
    Button,
    Select,
    useToast,
} from "@chakra-ui/react";
import moment from "moment";
import axios from "axios";
import { useAuth } from "../auth/AuthProvider";
import { useNavigate } from "react-router-dom";
import { server } from "../util/connect";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "../css/CustomTable.css";
import { callAddFont } from "../js/THSarabunNew-normal";

const PatientHist12Months = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const toast = useToast();

    const [doctors, setDoctors] = useState([]);
    const [selectedDoctorID, setSelectedDoctorID] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isDoctorMonthlyLoading, setIsDoctorMonthlyLoading] = useState(false);
    const [isDownloading, setDownloading] = useState(false);

    const [rollingSummary, setRollingSummary] = useState([]); // 12 rows (months)
    const [doctorMonthlyRows, setDoctorMonthlyRows] = useState([]); // many rows (doctors)
    const [doctorMonthlyGrandTotal, setDoctorMonthlyGrandTotal] = useState(0);

    const thMonthShort = ["ม.ค", "ก.พ", "มี.ค", "เม.ย", "พ.ค", "มิ.ย", "ก.ค", "ส.ค", "ก.ย", "ต.ค", "พ.ย", "ธ.ค"];


    // ✅ 12 months window (oldest -> newest)
    const months12 = useMemo(() => {
        return Array.from({ length: 12 }, (_, i) =>
            moment().startOf("month").subtract(11 - i, "months")
        );
    }, []);

    const monthLabels = useMemo(() => {
        return months12.map((m) => `${thMonthShort[m.month()]} ${m.year()}`);
      }, [months12, thMonthShort]);
      

    const checkNull = (val) => (val === undefined || val === "" || val === 0 ? "" : val);

    const getDoctorNameFromID = () => {
        let name = "";
        doctors.forEach((d) => {
            if (`${d.id}` === `${selectedDoctorID}`) {
                name = d.first_name;
            }
        });
        return name;
    };

    // โหลดรายชื่อแพทย์
    const loadDoctors = (departmentId) => {
        axios
            .post(`${server}/daily_record/list_doctors`, { department_id: departmentId })
            .then((res) => setDoctors(res.data.doctors || []))
            .catch((e) => console.log(e));
    };

    // ====== 1) ตาราง: สรุปยอดแต่ละรายการย้อนหลัง 12 เดือน ======
    const loadRolling12Summary = async (departmentId) => {
        try {
            setIsLoading(true);

            const doctorName = getDoctorNameFromID();
            const responses = await Promise.all(
                months12.map((mm) => {
                    const year = mm.year();
                    const month = mm.format("MM");
                    return axios
                        .post(`${server}/daily_record/record_hist`, {
                            withCredentials: true,
                            department_id: departmentId,
                            doctor_id: selectedDoctorID || "",
                            doctor_name: doctorName,
                            year,
                            month,
                        })
                        .then((res) => ({
                            key: mm.format("YYYY-MM"),
                            monthMoment: mm,
                            records: (res.data && res.data.records) || [],
                        }))
                        .catch(() => ({
                            key: mm.format("YYYY-MM"),
                            monthMoment: mm,
                            records: [],
                        }));
                })
            );

            const rows = responses.map(({ monthMoment, records }) => {
                // ถ้าไม่เลือกหมอ -> sum_xxx, ถ้าเลือกหมอ -> xxx
                const sumField = (field) => {
                    const key = !selectedDoctorID ? `sum_${field}` : field;
                    return records.reduce((sum, r) => sum + (r[key] || 0), 0);
                };

                const oldO = sumField("old_o_case");
                const newO = sumField("new_o_case");
                const oldR = sumField("old_r_case");
                const newR = sumField("new_r_case");
                const admitO = sumField("admit_o_case");
                const admitR = sumField("admit_r_case");

                return {
                    monthMoment,
                    old_o_case: oldO,
                    new_o_case: newO,
                    o_total: oldO + newO,
                    old_r_case: oldR,
                    new_r_case: newR,
                    r_total: oldR + newR,
                    or_total: oldO + newO + oldR + newR,
                    lab_num: sumField("lab_num"),
                    xray_num: sumField("xray_num"),
                    ekg_num: sumField("ekg_num"),
                    abi_num: sumField("abi_num"),
                    obesity_num: sumField("obesity_num"),
                    treatment_num: sumField("treatment_num"),
                    refer_num: sumField("refer_num"),
                    ct_num: sumField("ct_num"),
                    pft_num: sumField("pft_num"),
                    blood_transfusion_num: sumField("blood_transfusion_num"),
                    or_num: sumField("or_num"),
                    chemo_num: sumField("chemo_num"),
                    blood_letting_num: sumField("blood_letting_num"),
                    lung_tap_num: sumField("lung_tap_num"),
                    bone_marrow_tap_num: sumField("bone_marrow_tap_num"),
                    vacc_rsv_num: sumField("vacc_rsv_num"),
                    vacc_flu_num: sumField("vacc_flu_num"),
                    vacc_ipd_num: sumField("vacc_ipd_num"),
                    vacc_dengue_num: sumField("vacc_dengue_num"),
                    vacc_zoster_num: sumField("vacc_zoster_num"),
                    admit_o_case: admitO,
                    admit_r_case: admitR,
                    admit_total: admitO + admitR,
                    check_up: sumField("check_up"),
                };
            });

            setRollingSummary(rows);
        } catch (err) {
            console.error("loadRolling12Summary error", err);
            setRollingSummary([]);
        } finally {
            setIsLoading(false);
        }
    };

    const getRollingTotal = (field) =>
        rollingSummary.reduce((sum, r) => sum + (r[field] || 0), 0);

    // ====== 2) ตาราง: O+R รายเดือน แยกตามแพทย์ ย้อนหลัง 12 เดือน ======
    // NOTE: ตารางนี้ “แยกตามแพทย์” -> ควรเป็นภาพรวม (ไม่ apply selectedDoctorID)
    const loadDoctorMonthly12Summary = async (departmentId) => {
        try {
            setIsDoctorMonthlyLoading(true);

            const doctorMap = {};
            const responses = await Promise.all(
                months12.map((mm, idx) => {
                    const year = mm.year();
                    const month = mm.format("MM");
                    return axios
                        .post(`${server}/daily_record/record_hist`, {
                            withCredentials: true,
                            department_id: departmentId,
                            doctor_id: "", // force all doctors
                            doctor_name: "",
                            year,
                            month,
                        })
                        .then((res) => ({
                            monthIndex: idx,
                            records: (res.data && res.data.records) || [],
                        }))
                        .catch(() => ({
                            monthIndex: idx,
                            records: [],
                        }));
                })
            );

            responses.forEach(({ monthIndex, records }) => {
                records.forEach((r) => {
                    const doctorName = r.first_name || "-";

                    if (!doctorMap[doctorName]) {
                        doctorMap[doctorName] = {
                            name: doctorName,
                            monthly: Array(12).fill(0),
                        };
                    }

                    // ✅ cell value = O+R (sum fields)
                    const oOld = r.sum_old_o_case || 0;
                    const oNew = r.sum_new_o_case || 0;
                    const rOld = r.sum_old_r_case || 0;
                    const rNew = r.sum_new_r_case || 0;
                    const orTotal = oOld + oNew + rOld + rNew;

                    doctorMap[doctorName].monthly[monthIndex] += orTotal;
                });
            });

            const rows = Object.values(doctorMap);

            const grandTotal = rows.reduce(
                (sum, row) => sum + row.monthly.reduce((s, v) => s + v, 0),
                0
            );

            rows.forEach((row) => {
                const total = row.monthly.reduce((s, v) => s + v, 0);
                row.total = total;
                row.percent = grandTotal ? (total * 100) / grandTotal : 0;
            });

            // sort: total desc, special at bottom
            rows.sort((a, b) => {
                const special = ["แพทย์อื่นๆ", "ไม่พบแพทย์"];

                const aIsSpecial = special.includes(a.name);
                const bIsSpecial = special.includes(b.name);

                if (aIsSpecial && !bIsSpecial) return 1;
                if (!aIsSpecial && bIsSpecial) return -1;
                if (aIsSpecial && bIsSpecial) return a.name.localeCompare(b.name, "th");

                return b.total - a.total;
            });

            setDoctorMonthlyRows(rows);
            setDoctorMonthlyGrandTotal(grandTotal);
        } catch (err) {
            console.error("loadDoctorMonthly12Summary error", err);
            setDoctorMonthlyRows([]);
            setDoctorMonthlyGrandTotal(0);
        } finally {
            setIsDoctorMonthlyLoading(false);
        }
    };

    // ===== PDF builders (สองตาราง) =====
    const shareCancelSafe = (err) =>
        err?.name === "AbortError" || err?.name === "NotAllowedError";

    const buildRollingSummaryPdfDoc = () => {
        if (!rollingSummary.length) return null;

        jsPDF.API.events.push(["addFonts", callAddFont]);
        const doc = new jsPDF("landscape");

        const title = `สรุปยอดแต่ละรายการย้อนหลัง 12 เดือน` +
            (selectedDoctorID ? ` (${getDoctorNameFromID()})` : ` (${user?.department || ""})`);

        const columns = [
            "เดือน",
            "O เก่า", "O ใหม่", "O รวม",
            "R เก่า", "R ใหม่", "R รวม",
            "รวม O+R",
            "LAB", "X-RAY", "EKG", "ABI", "OBESITY", "TREAT.", "REFER",
            "CT", "PFT", "ให้เลือด", "OR", "CHEMO", "B. LETTING", "เจาะปอด", "เจาะไขกระดูก",
            "V. RSV", "V. FLU", "V. IPD", "V. DENGUE", "V. ZOSTER",
            "O ADM.", "R ADM.", "ADM. รวม", "CHECK UP",
        ];

        const rows = rollingSummary.map((row) => ([
            `${thMonthShort[row.monthMoment.month()]} ${row.monthMoment.year()}`,
            checkNull(row.old_o_case),
            checkNull(row.new_o_case),
            checkNull(row.o_total),
            checkNull(row.old_r_case),
            checkNull(row.new_r_case),
            checkNull(row.r_total),
            checkNull(row.or_total),
            checkNull(row.lab_num),
            checkNull(row.xray_num),
            checkNull(row.ekg_num),
            checkNull(row.abi_num),
            checkNull(row.obesity_num),
            checkNull(row.treatment_num),
            checkNull(row.refer_num),
            checkNull(row.ct_num),
            checkNull(row.pft_num),
            checkNull(row.blood_transfusion_num),
            checkNull(row.or_num),
            checkNull(row.chemo_num),
            checkNull(row.blood_letting_num),
            checkNull(row.lung_tap_num),
            checkNull(row.bone_marrow_tap_num),
            checkNull(row.vacc_rsv_num),
            checkNull(row.vacc_flu_num),
            checkNull(row.vacc_ipd_num),
            checkNull(row.vacc_dengue_num),
            checkNull(row.vacc_zoster_num),
            checkNull(row.admit_o_case),
            checkNull(row.admit_r_case),
            checkNull(row.admit_total),
            checkNull(row.check_up),
        ]));

        // total row
        rows.push([
            "รวม",
            checkNull(getRollingTotal("old_o_case")),
            checkNull(getRollingTotal("new_o_case")),
            checkNull(getRollingTotal("o_total")),
            checkNull(getRollingTotal("old_r_case")),
            checkNull(getRollingTotal("new_r_case")),
            checkNull(getRollingTotal("r_total")),
            checkNull(getRollingTotal("or_total")),
            checkNull(getRollingTotal("lab_num")),
            checkNull(getRollingTotal("xray_num")),
            checkNull(getRollingTotal("ekg_num")),
            checkNull(getRollingTotal("abi_num")),
            checkNull(getRollingTotal("obesity_num")),
            checkNull(getRollingTotal("treatment_num")),
            checkNull(getRollingTotal("refer_num")),
            checkNull(getRollingTotal("ct_num")),
            checkNull(getRollingTotal("pft_num")),
            checkNull(getRollingTotal("blood_transfusion_num")),
            checkNull(getRollingTotal("or_num")),
            checkNull(getRollingTotal("chemo_num")),
            checkNull(getRollingTotal("blood_letting_num")),
            checkNull(getRollingTotal("lung_tap_num")),
            checkNull(getRollingTotal("bone_marrow_tap_num")),
            checkNull(getRollingTotal("vacc_rsv_num")),
            checkNull(getRollingTotal("vacc_flu_num")),
            checkNull(getRollingTotal("vacc_ipd_num")),
            checkNull(getRollingTotal("vacc_dengue_num")),
            checkNull(getRollingTotal("vacc_zoster_num")),
            checkNull(getRollingTotal("admit_o_case")),
            checkNull(getRollingTotal("admit_r_case")),
            checkNull(getRollingTotal("admit_total")),
            checkNull(getRollingTotal("check_up")),
        ]);

        const margin = 5;
        doc.setFont("THSarabunNew", "normal");
        doc.setFontSize(12);
        doc.text(title, margin, margin + 4);

        const pageW = doc.internal.pageSize.getWidth();
        doc.autoTable({
            head: [columns],
            body: rows,
            startY: margin + 8,
            margin: { top: margin + 4, left: margin, right: margin, bottom: margin },
            tableWidth: pageW - margin * 2,
            styles: { font: "THSarabunNew", fontSize: 8, cellPadding: 1, overflow: "ellipsize" },
            headStyles: { fillColor: [0, 84, 168], textColor: 255 },
            theme: "grid",
        });

        const filename = `สรุปยอดย้อนหลัง12เดือน_${moment().format("DD-MM-YYYY_HH_mm_ss")}.pdf`;
        return { doc, filename };
    };

    const exportRollingSummaryPdf = () => {
        const built = buildRollingSummaryPdfDoc();
        if (!built) return;
        setDownloading(true);
        built.doc.save(built.filename);
        setDownloading(false);
    };

    const shareRollingSummaryPdf = async () => {
        const built = buildRollingSummaryPdfDoc();
        if (!built) return;
        setDownloading(true);
        try {
            const blob = await built.doc.output("blob");
            const file = new File([blob], built.filename, { type: "application/pdf" });

            if (navigator.canShare?.({ files: [file] })) {
                await navigator.share({ files: [file], title: built.filename });
            } else {
                built.doc.save(built.filename);
            }
        } catch (err) {
            if (!shareCancelSafe(err)) {
                toast({
                    title: "แชร์ไฟล์ไม่สำเร็จ",
                    description: err.message,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } finally {
            setDownloading(false);
        }
    };

    const buildDoctorMonthly12PdfDoc = () => {
        if (!doctorMonthlyRows.length) return null;

        jsPDF.API.events.push(["addFonts", callAddFont]);
        const doc = new jsPDF("landscape");

        const title = `สถิติยอดคนไข้ (O+R) รายเดือน แยกตามแพทย์ ย้อนหลัง 12 เดือน (${user?.department || ""})`;

        const columns = ["แพทย์", "จำนวน", "% จากยอดรวม", ...monthLabels];

        const rows = doctorMonthlyRows.map((row) => ([
            row.name,
            row.total ? `${row.total.toLocaleString()}` : "",
            `${row.percent.toFixed(1)}%`,
            ...row.monthly.map((v) => checkNull(v)),
        ]));

        const monthTotals = months12.map((_, idx) =>
            doctorMonthlyRows.reduce((sum, row) => sum + (row.monthly[idx] || 0), 0)
        );

        rows.push([
            "รวม",
            checkNull(doctorMonthlyGrandTotal),
            "",
            ...monthTotals.map((v) => checkNull(v)),
        ]);

        const margin = 5;
        doc.setFont("THSarabunNew", "normal");
        doc.setFontSize(12);
        doc.text(title, margin, margin + 4);

        const pageW = doc.internal.pageSize.getWidth();
        doc.autoTable({
            head: [columns],
            body: rows,
            startY: margin + 8,
            margin: { top: margin + 4, left: margin, right: margin, bottom: margin },
            tableWidth: pageW - margin * 2,
            styles: { font: "THSarabunNew", fontSize: 8, cellPadding: 1, overflow: "ellipsize" },
            headStyles: { fillColor: [0, 84, 168], textColor: 255 },
            theme: "grid",
        });

        const filename = `สถิติแพทย์ย้อนหลัง12เดือน_${moment().format("DD-MM-YYYY_HH_mm_ss")}.pdf`;
        return { doc, filename };
    };

    const exportDoctorMonthly12Pdf = () => {
        const built = buildDoctorMonthly12PdfDoc();
        if (!built) return;
        setDownloading(true);
        built.doc.save(built.filename);
        setDownloading(false);
    };

    const shareDoctorMonthly12Pdf = async () => {
        const built = buildDoctorMonthly12PdfDoc();
        if (!built) return;
        setDownloading(true);
        try {
            const blob = await built.doc.output("blob");
            const file = new File([blob], built.filename, { type: "application/pdf" });

            if (navigator.canShare?.({ files: [file] })) {
                await navigator.share({ files: [file], title: built.filename });
            } else {
                built.doc.save(built.filename);
            }
        } catch (err) {
            if (!shareCancelSafe(err)) {
                toast({
                    title: "แชร์ไฟล์ไม่สำเร็จ",
                    description: err.message,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } finally {
            setDownloading(false);
        }
    };

    // init
    useEffect(() => {
        if (!user) return;
        if (!user.department_id) return;
        if (!doctors.length) loadDoctors(user.department_id);
    }, [user]);

    // reload data when doctor filter changes
    useEffect(() => {
        if (!user || !user.department_id) return;
        // 1) rolling summary respects selectedDoctorID
        loadRolling12Summary(user.department_id);
        // 2) doctor-table always overall (ignores selectedDoctorID)
        loadDoctorMonthly12Summary(user.department_id);
    }, [user, selectedDoctorID]);

    return (
        <Box px={{ base: 0, md: 4 }} bgColor="blue.50" pb={10}>
            <Flex direction="column" align="center">
                <Text my={2} fontWeight={700} fontSize={18}>
                    รายงานย้อนหลัง 12 เดือน {user ? user.department : ""}
                </Text>

                <Flex my={2} mx={{ base: 2, md: 4 }} align="center" gap={2}>
                    <Text fontSize="sm">ตัวกรองแพทย์ (มีผลกับตารางสรุปรายการเท่านั้น):</Text>
                    <Select
                        size="sm"
                        w="220px"
                        value={selectedDoctorID}
                        onChange={(e) => setSelectedDoctorID(e.target.value)}
                    >
                        <option value="">แพทย์ทั้งหมด</option>
                        {doctors.map((d) => (
                            <option key={`doctor_${d.id}`} value={d.id}>
                                {`${d.first_name} ${d.last_name}`}
                            </option>
                        ))}
                    </Select>
                </Flex>
            </Flex>

            {/* ===== Table 1 ===== */}
            <Box mt={4}>
                <Heading ml={1} size="sm" mb={2}>
                    สรุปยอดแต่ละรายการย้อนหลัง 12 เดือน
                    {selectedDoctorID && <> ({getDoctorNameFromID()})</>}
                </Heading>

                {isLoading ? (
                    <Flex mt={2}>
                        <Spinner mr={2} />
                        <Text>Loading data...</Text>
                    </Flex>
                ) : rollingSummary.length ? (
                    <>
                        <Box overflowX="auto">
                            <Table size="xs" className="custom-table">
                                <Thead>
                                    <Tr>
                                        <Th px={2} minW="110px">เดือน</Th>
                                        <Th px={2}>O เก่า</Th>
                                        <Th px={2}>O ใหม่</Th>
                                        <Th px={2}>O รวม</Th>
                                        <Th px={2}>R เก่า</Th>
                                        <Th px={2}>R ใหม่</Th>
                                        <Th px={2}>R รวม</Th>
                                        <Th px={2}>รวม O+R</Th>
                                        <Th px={2}>LAB</Th>
                                        <Th px={2}>X-RAY</Th>
                                        <Th px={2}>EKG</Th>
                                        <Th px={2}>ABI</Th>
                                        <Th px={2}>OBESITY</Th>
                                        <Th px={2}>TREAT.</Th>
                                        <Th px={2}>REFER</Th>
                                        <Th px={2}>CT</Th>
                                        <Th px={2}>PFT</Th>
                                        <Th px={2}>ให้เลือด</Th>
                                        <Th px={2}>OR</Th>
                                        <Th px={2}>CHEMO</Th>
                                        <Th px={2}>BLOOD LETTING</Th>
                                        <Th px={2}>เจาะปอด</Th>
                                        <Th px={2}>เจาะไขกระดูก</Th>
                                        <Th px={2}>V. RSV</Th>
                                        <Th px={2}>V. FLU</Th>
                                        <Th px={2}>V. IPD</Th>
                                        <Th px={2}>V. DENGUE</Th>
                                        <Th px={2}>V. ZOSTER</Th>
                                        <Th px={2}>O ADM.</Th>
                                        <Th px={2}>R ADM.</Th>
                                        <Th px={2}>ADM. รวม</Th>
                                        <Th px={2}>CHECK UP</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {rollingSummary.map((row, idx) => (
                                        <Tr key={`m_${idx}`}>
                                            <Td px={2}>
                                                {`${thMonthShort[row.monthMoment.month()]} ${row.monthMoment.year()}`}
                                            </Td>
                                            <Td px={2}>{checkNull(row.old_o_case)}</Td>
                                            <Td px={2}>{checkNull(row.new_o_case)}</Td>
                                            <Td px={2}>{checkNull(row.o_total)}</Td>
                                            <Td px={2}>{checkNull(row.old_r_case)}</Td>
                                            <Td px={2}>{checkNull(row.new_r_case)}</Td>
                                            <Td px={2}>{checkNull(row.r_total)}</Td>
                                            <Td px={2}>{checkNull(row.or_total)}</Td>
                                            <Td px={2}>{checkNull(row.lab_num)}</Td>
                                            <Td px={2}>{checkNull(row.xray_num)}</Td>
                                            <Td px={2}>{checkNull(row.ekg_num)}</Td>
                                            <Td px={2}>{checkNull(row.abi_num)}</Td>
                                            <Td px={2}>{checkNull(row.obesity_num)}</Td>
                                            <Td px={2}>{checkNull(row.treatment_num)}</Td>
                                            <Td px={2}>{checkNull(row.refer_num)}</Td>
                                            <Td px={2}>{checkNull(row.ct_num)}</Td>
                                            <Td px={2}>{checkNull(row.pft_num)}</Td>
                                            <Td px={2}>{checkNull(row.blood_transfusion_num)}</Td>
                                            <Td px={2}>{checkNull(row.or_num)}</Td>
                                            <Td px={2}>{checkNull(row.chemo_num)}</Td>
                                            <Td px={2}>{checkNull(row.blood_letting_num)}</Td>
                                            <Td px={2}>{checkNull(row.lung_tap_num)}</Td>
                                            <Td px={2}>{checkNull(row.bone_marrow_tap_num)}</Td>
                                            <Td px={2}>{checkNull(row.vacc_rsv_num)}</Td>
                                            <Td px={2}>{checkNull(row.vacc_flu_num)}</Td>
                                            <Td px={2}>{checkNull(row.vacc_ipd_num)}</Td>
                                            <Td px={2}>{checkNull(row.vacc_dengue_num)}</Td>
                                            <Td px={2}>{checkNull(row.vacc_zoster_num)}</Td>
                                            <Td px={2}>{checkNull(row.admit_o_case)}</Td>
                                            <Td px={2}>{checkNull(row.admit_r_case)}</Td>
                                            <Td px={2}>{checkNull(row.admit_total)}</Td>
                                            <Td px={2}>{checkNull(row.check_up)}</Td>
                                        </Tr>
                                    ))}

                                    <Tr bgColor="blue.50" fontWeight="bold">
                                        <Td px={2}>รวม</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("old_o_case"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("new_o_case"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("o_total"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("old_r_case"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("new_r_case"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("r_total"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("or_total"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("lab_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("xray_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("ekg_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("abi_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("obesity_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("treatment_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("refer_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("ct_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("pft_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("blood_transfusion_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("or_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("chemo_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("blood_letting_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("lung_tap_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("bone_marrow_tap_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("vacc_rsv_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("vacc_flu_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("vacc_ipd_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("vacc_dengue_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("vacc_zoster_num"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("admit_o_case"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("admit_r_case"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("admit_total"))}</Td>
                                        <Td px={2}>{checkNull(getRollingTotal("check_up"))}</Td>
                                    </Tr>
                                </Tbody>
                            </Table>
                        </Box>

                        <Flex w="100%" justify="center" mt={2} mb={2} gap={2}>
                            <Button
                                size="sm"
                                colorScheme="green"
                                variant="outline"
                                onClick={exportRollingSummaryPdf}
                                isDisabled={isDownloading}
                            >
                                Export PDF
                            </Button>
                            <Button
                                size="sm"
                                colorScheme="blue"
                                variant="outline"
                                onClick={shareRollingSummaryPdf}
                                isDisabled={isDownloading || !navigator.share}
                            >
                                Share PDF
                            </Button>
                        </Flex>
                    </>
                ) : (
                    <Text mt={2}>ไม่มีข้อมูลย้อนหลัง 12 เดือน</Text>
                )}
            </Box>

            {/* ===== Table 2 ===== */}
            <Box mt={8}>
                <Heading ml={1} size="sm" mb={2}>
                    สถิติยอดคนไข้ (O+R) รายเดือน แยกตามแพทย์ ย้อนหลัง 12 เดือน
                </Heading>

                {isDoctorMonthlyLoading ? (
                    <Flex mt={2}>
                        <Spinner mr={2} />
                        <Text>Loading data...</Text>
                    </Flex>
                ) : doctorMonthlyRows.length ? (
                    <>
                        <Box overflowX="auto">
                            <Table size="xs" className="custom-table">
                                <Thead>
                                    <Tr>
                                        <Th px={2} minW="160px">แพทย์</Th>
                                        <Th px={2} minW="90px">จำนวน</Th>
                                        <Th px={2} minW="90px">% จากยอดรวม</Th>
                                        {monthLabels.map((m) => (
                                            <Th key={m} px={2}>{m}</Th>
                                        ))}
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {doctorMonthlyRows.map((row) => (
                                        <Tr key={row.name}>
                                            <Td px={2}>{row.name}</Td>
                                            <Td px={2}>{row.total ? `${row.total.toLocaleString()}` : ""}</Td>
                                            <Td px={2}>{`${row.percent.toFixed(1)}%`}</Td>
                                            {row.monthly.map((val, idx) => (
                                                <Td key={idx} px={2}>{checkNull(val)}</Td>
                                            ))}
                                        </Tr>
                                    ))}

                                    <Tr bgColor="blue.50" fontWeight="bold">
                                        <Td px={2}>รวม</Td>
                                        <Td px={2}>{checkNull(doctorMonthlyGrandTotal)}</Td>
                                        <Td px={2}></Td>
                                        {months12.map((_, idx) => (
                                            <Td key={idx} px={2}>
                                                {checkNull(
                                                    doctorMonthlyRows.reduce(
                                                        (sum, row) => sum + (row.monthly[idx] || 0),
                                                        0
                                                    )
                                                )}
                                            </Td>
                                        ))}
                                    </Tr>
                                </Tbody>
                            </Table>
                        </Box>

                        <Flex w="100%" justify="center" mt={2} mb={2} gap={2}>
                            <Button
                                size="sm"
                                colorScheme="green"
                                variant="outline"
                                onClick={exportDoctorMonthly12Pdf}
                                isDisabled={isDownloading}
                            >
                                Export PDF
                            </Button>
                            <Button
                                size="sm"
                                colorScheme="blue"
                                variant="outline"
                                onClick={shareDoctorMonthly12Pdf}
                                isDisabled={isDownloading || !navigator.share}
                            >
                                Share PDF
                            </Button>
                        </Flex>
                    </>
                ) : (
                    <Text mt={2}>ไม่มีข้อมูลย้อนหลัง 12 เดือน</Text>
                )}
            </Box>
        </Box>
    );
};

export default PatientHist12Months;
